import { getUniqueId } from '@/editor/utils';
import { TGlobalStores } from '@/editor/stores';
import type { IFurniture } from '@/editor/data/model/furniture';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { EEntityStatus } from '@/editor/types/entity';
import { CommonController2d } from '../CommonController';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import FurnitureModel from '@/editor/entities/2d/furnitureModel';
import { FurnitureAbsorbManager } from '@/editor/manager/absorb/box/furnitureAbsorbManager';

export class DrawFurniture extends CommonController2d {
  private _furnitureData: (IFurniture & { model2d: string }) | null = null;
  private _furnitureModel = new FurnitureModel();
  private _rulerModel = new RulerModel();
  private _rayDetectOutlines: { start: IPoint2d; end: IPoint2d }[] = [];
  private _furnitureAbsorbManager = new FurnitureAbsorbManager();

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._updataAbsorbData();
  }

  private _updataAbsorbData() {
    this._rayDetectOutlines = getBoxRayDetectOutlines();
    this._furnitureAbsorbManager.updateData();
  }

  private _getDefaultModelData() {
    if (!this._gs.operateInfo.extraState?.furnitureData) return;

    const modelInfo = this._gs.modelStore.getModel(this._gs.operateInfo.extraState.furnitureData.modelId);
    if (!modelInfo) return;

    const virtualData = this._gs.editorStateStore.furnitureVirtualData ?? {
      offGround: modelInfo.properties.offGround,
      width: modelInfo.properties.width,
      thickness: modelInfo.properties.thickness,
      height: modelInfo.properties.height,
      rotation: 0
    };

    return {
      modelId: modelInfo.id,
      offGround: virtualData.offGround,
      width: virtualData.width,
      thickness: virtualData.thickness,
      height: virtualData.height,
      rotation: virtualData.rotation,
      model2d: modelInfo.model2d
    };
  }

  protected override mouseMove = (e: MouseEvent) => {
    const modelData = this._getDefaultModelData();
    if (!modelData) return;

    const mousePosition = this.getMousePos(e);

    const box = {
      center: mousePosition,
      width: modelData.width,
      thickness: modelData.thickness,
      rotation: modelData.rotation
    };

    const absorbResult = this._furnitureAbsorbManager.calculateThenRender(box);

    if (absorbResult) {
      box.center = absorbResult.absorbPoint;
      box.rotation = absorbResult.rotation;
    }

    this._furnitureData = {
      id: '',
      ...modelData,
      ...box
    };

    this.updateModel();
  };

  protected override mouseUp = (e: MouseEvent, isDragged: boolean) => {
    if (isDragged || !this._getDefaultModelData()) return;

    if (e.button === 2) {
      this.dispose();
      this.exit();
      return;
    }

    if (e.button !== 0 || !this._furnitureData) return;

    const fd = { ...this._furnitureData, id: getUniqueId() };
    Reflect.deleteProperty(fd, 'model2d');
    this._gs.furnitureOperation.addFurniture(fd);

    this._updataAbsorbData();
  };

  private updateModel() {
    if (!this._furnitureData) {
      this._rulerModel.clear();
      return;
    }

    this._furnitureModel.update({ ...this._furnitureData, selected: false, status: EEntityStatus.normal });

    const rulerInfos = calculateBoxRulerInfos(this._furnitureData, this._rayDetectOutlines);
    this._rulerModel.updateModel(rulerInfos);
  }

  clear() {
    this._rulerModel.clear();
    this._furnitureModel.clear();
    this._furnitureAbsorbManager.clearModel();
  }

  override dispose() {
    this.clear();
    this._rulerModel.dispose();
    this._furnitureModel.dispose();
    this._furnitureAbsorbManager.dispose();
    super.dispose();
  }
}
